import { Client } from '@modelcontextprotocol/sdk/client/index.js';
import { StreamableHTTPClientTransport } from '@modelcontextprotocol/sdk/client/streamableHttp.js';
import { DynamicStructuredTool, type StructuredToolInterface } from '@langchain/core/tools';
import { z } from 'zod';
import { logger } from '../../utils/logger.js';
import { formatToolResult } from '../types.js';
import { withTimeout, SUB_TOOL_TIMEOUT_MS } from './utils.js';
import { recordCreditUsage } from './credit-usage.js';
import { estimateMcpCredits } from './credit-costs.js';

/**
 * Thin client for the Drishti MCP server, used as the primary market-data
 * capability layer exposed to the agent.
 *
 * Transport: hosted Streamable HTTP only (DRISHTI_MCP_URL), reusing the REST
 * API key as the X-API-Key header. Everything here degrades gracefully: when
 * MCP is unconfigured or the connection fails, functions return null/[] and
 * never throw into the agent loop.
 *
 * We hand-roll the LangChain adapter (rather than @langchain/mcp-adapters) to
 * avoid pulling in @langchain/langgraph, which requires a newer @langchain/core
 * than the version this project pins.
 */

const MCP_CONNECT_TIMEOUT_MS = 10_000;

/** MCP tool definition shape returned by listTools(). */
interface McpToolDef {
  name: string;
  description?: string;
  inputSchema?: Record<string, unknown>;
}

/**
 * Drishti MCP endpoint. This is a fixed, hosted URL, so it is hardcoded and does
 * not require any .env entry; an env override is still honored if present.
 * The server authenticates with a Bearer token (the Drishti API key).
 */
export const DRISHTI_MCP_URL = process.env.DRISHTI_MCP_URL || 'https://mcp.drishti.manasija.in';

/** True when a Drishti MCP endpoint is available (always true — URL is baked in). */
export function isMcpConfigured(): boolean {
  return !!DRISHTI_MCP_URL;
}

// Lazy singletons. `null` client with `attempted=true` means we tried and
// failed (or it's unconfigured) — we won't retry within the process to avoid
// repeatedly stalling on a dead endpoint.
let clientPromise: Promise<Client | null> | null = null;
let toolsCache: StructuredToolInterface[] | null = null;

async function connect(): Promise<Client | null> {
  if (!isMcpConfigured()) return null;

  const url = DRISHTI_MCP_URL;
  const apiKey = process.env.DRISHTI_API_KEY || '';

  try {
    const transport = new StreamableHTTPClientTransport(new URL(url), {
      requestInit: {
        headers: apiKey ? { Authorization: `Bearer ${apiKey}` } : {},
      },
    });
    const client = new Client(
      { name: 'max', version: '1.0.0' },
      { capabilities: {} },
    );
    await withTimeout(client.connect(transport), MCP_CONNECT_TIMEOUT_MS, 'Drishti MCP connect');
    logger.info(`[Drishti MCP] connected: ${url}`);
    return client;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logger.warn(`[Drishti MCP] connection failed: ${message}`);
    return null;
  }
}

/**
 * Get the connected MCP client (lazy singleton). Returns null when MCP is
 * unconfigured or the connection failed — callers must handle null.
 */
export function getMcpClient(): Promise<Client | null> {
  if (!clientPromise) {
    clientPromise = connect();
  }
  return clientPromise;
}

/**
 * Convert one MCP tool definition into a LangChain StructuredTool that invokes
 * it over the shared client. The raw MCP JSON Schema is passed straight through
 * as the tool schema (core >=1 accepts JSON Schema), so no zod conversion is
 * needed. Result content blocks are flattened to text and wrapped in the same
 * formatToolResult envelope the REST tools use.
 */
function toLangChainTool(def: McpToolDef): StructuredToolInterface {
  return new DynamicStructuredTool({
    name: def.name,
    description: def.description ?? `Drishti MCP tool: ${def.name}`,
    // Keep activated MCP tools permissive. `search_tools` exposes parameter
    // names, and the MCP server remains the source of truth for validation.
    // This avoids burning an agent turn on local JSON-schema mismatches such as
    // symbols="TCS" vs symbols=["TCS"].
    schema: z.object({}).passthrough(),
    func: async (args: Record<string, unknown>) => {
      return callMcpToolByName(def.name, normalizeMcpArgs(def.name, args, def.inputSchema));
    },
  });
}

export function normalizeMcpArgs(
  name: string,
  args: Record<string, unknown>,
  inputSchema?: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...args };
  for (const key of ['symbols', 'scrip_codes', 'categories', 'type']) {
    const value = out[key];
    if (typeof value === 'string' && value.trim()) {
      out[key] = [key === 'symbols' ? value.trim().toUpperCase() : value.trim()];
    }
  }
  if (typeof out.symbol === 'string' && !out.symbols) {
    out.symbols = [out.symbol.trim().toUpperCase()];
    delete out.symbol;
  }
  if (name === 'list_earnings') {
    delete out.detailed;
    delete out.lookback_days;
  }
  const allowed = schemaPropertyNames(inputSchema);
  if (allowed && allowed.size > 0) {
    for (const key of Object.keys(out)) {
      if (!allowed.has(key)) delete out[key];
    }
  }
  return out;
}

function schemaPropertyNames(schema?: Record<string, unknown>): Set<string> | null {
  const properties = schema?.properties;
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
    return null;
  }
  return new Set(Object.keys(properties as Record<string, unknown>));
}

/**
 * Load the MCP tool catalog as LangChain tools (memoized). Returns [] when MCP
 * is unavailable. These tools are surfaced on demand by the `search_tools` tool
 * so every schema does not sit in the prompt at startup.
 */
export async function getMcpTools(): Promise<StructuredToolInterface[]> {
  if (toolsCache) return toolsCache;
  const client = await getMcpClient();
  if (!client) {
    toolsCache = [];
    return toolsCache;
  }
  try {
    const { tools } = await withTimeout(client.listTools(), SUB_TOOL_TIMEOUT_MS, 'Drishti MCP listTools');
    toolsCache = (tools as McpToolDef[]).map(toLangChainTool);
    return toolsCache;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logger.warn(`[Drishti MCP] listTools failed: ${message}`);
    toolsCache = [];
    return toolsCache;
  }
}

/** Flatten an MCP callTool content array into a single text string. */
function flattenMcpContent(result: unknown): string {
  if (!result || typeof result !== 'object') return String(result ?? '');
  const content = (result as { content?: unknown }).content;
  if (!Array.isArray(content)) {
    // Some servers return structuredContent directly.
    const structured = (result as { structuredContent?: unknown }).structuredContent;
    return structured !== undefined ? JSON.stringify(structured) : JSON.stringify(result);
  }
  const parts = content
    .map((block) => {
      if (block && typeof block === 'object' && (block as { type?: string }).type === 'text') {
        return (block as { text?: string }).text ?? '';
      }
      return JSON.stringify(block);
    })
    .filter(Boolean);
  return parts.join('\n');
}

/**
 * Invoke a single MCP tool by name and return a formatToolResult-shaped string.
 * Throws if MCP is unavailable or the call errors — the fallback path catches
 * this and surfaces a graceful message.
 */
export async function callMcpToolByName(name: string, args: Record<string, unknown>): Promise<string> {
  const client = await getMcpClient();
  if (!client) {
    throw new Error('Drishti MCP is not available');
  }
  const result = await withTimeout(
    client.callTool({ name, arguments: args }),
    SUB_TOOL_TIMEOUT_MS,
    `Drishti MCP ${name}`,
  );
  if (result && typeof result === 'object' && (result as { isError?: boolean }).isError) {
    throw new Error(`MCP tool ${name} returned an error: ${flattenMcpContent(result)}`);
  }
  recordCreditUsage(estimateMcpCredits(name, args));
  const text = flattenMcpContent(result);
  // Try to preserve a data envelope; if the MCP tool already returned JSON,
  // pass it through as parsed data so downstream sees structured content.
  try {
    return formatToolResult(JSON.parse(text), [`mcp://drishti/${name}`]);
  } catch {
    return formatToolResult(text, [`mcp://drishti/${name}`]);
  }
}

/** Test-only: reset memoized singletons. */
export function __resetMcpClientForTests(): void {
  clientPromise = null;
  toolsCache = null;
}
